import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import moment from "moment";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Footer from "../components/dashboard/footer";
import Header from "../components/dashboard/header";
import Sidebar from "../components/dashboard/sidebar";
import { contentContext } from "../context/contentContext";
import SessionPopup from "../pages/SessionPopup";
import TableLoader from "../components/tableLoader";
import RevenueFilters, { DATE_FORMAT, REVENUE_PRESETS, RevenuePageHeader, TIMEZONE, money, toFilterOptions, useRevenueFilters } from "../components/revenueFilters";
import { revenueMenuId } from "../utils/revenueMenu";
import "./revenue.css";

let { lambda, appname } = window.app

// /revenueReport returns one row per day with activity. Weeks and months are grouped
// here so switching granularity needs no refetch.
const TREND_FIELDS = ["totalRevenue", "planRevenue", "bookingRevenue", "refund", "disputeLost", "netRevenue",
    "clientsRevenue", "pendingPayout", "clientShareReversed", "stripeFee", "disputeFee", "spotsaverRevenue"];

const trendGroupKey = (iso, granularity) => {
    const d = moment(iso, DATE_FORMAT);
    if (granularity === "week") {
        const monday = d.clone().isoWeekday(1);
        const sunday = monday.clone().add(6, "days");
        const end = monday.month() === sunday.month() ? sunday.format("D") : sunday.format("MMM D");
        return { key: monday.valueOf(), label: `${monday.format("MMM D")}–${end}, ${sunday.format("YYYY")}` };
    }
    if (granularity === "month") {
        const start = d.clone().startOf("month");
        return { key: start.valueOf(), label: start.format("MMM YYYY") };
    }
    return { key: d.valueOf(), label: d.format(d.year() === moment().year() ? "ddd, MMM D" : "ddd, MMM D, YYYY") };
};

const GRANULARITY_TITLES = { day: "Daily breakdown", week: "Weekly breakdown", month: "Monthly breakdown" };
const TREND_MIN_DAYS = 7;
const PAGE_SIZES = [10, 25, 50];

// Where the money went: the split bar, its tiles, the chart and the table share these.
const SPLIT_COLORS = { clients: "#23945a", stripe: "#a08ce8", spotsaver: "#4a32d8" };

const percent = (part, whole) => (whole > 0.005 ? `${((part / whole) * 100).toFixed(1)}%` : "—");
const shortPercent = (part, whole) => (whole > 0.005 ? `${Math.round((part / whole) * 100)}%` : "0%");
const isZero = (v) => Math.abs(Number(v) || 0) < 0.005;

/** A money cell that fades when zero and turns red when negative. */
const Amount = ({ value, className = "" }) => {
    const n = Number(value) || 0;
    const tone = n <= -0.005 ? " revenue-negative" : isZero(n) ? " rev-zero" : "";
    return <span className={`${className}${tone}`}>{money(n)}</span>;
};

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rev-chart-tooltip">
            <p className="rev-chart-tooltip-title">{label}</p>
            {payload.slice().reverse().map((p) => (
                <p key={p.dataKey} className="rev-chart-tooltip-row">
                    <span className="rev-dot" style={{ background: p.color }} />
                    {p.name}<strong>{money(p.value)}</strong>
                </p>
            ))}
        </div>
    );
};

const csvEscape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename, rows) => {
    const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

const Revenue = () => {
    const navigate = useNavigate();
    const { setRoute, setActiveMenuId, userData } = useContext(contentContext);
    const [showSessionPopupup, setShowSessionPopupup] = useState(false);

    const filters = useRevenueFilters("today");
    const { datePreset, dateFrom, dateTo, client, location } = filters;
    const [granularity, setGranularity] = useState("day");
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

    const [report, setReport] = useState({ rows: [], summary: {}, plansIncluded: true });
    const [filterOptions, setFilterOptions] = useState({ clients: [], locations: [] });
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    useEffect(() => {
        if (!localStorage.getItem("token")) {
            navigate("/");
        }
        setRoute("client")
        userActivity();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // The Revenue menu's id comes from the menu migration (or the sidebar's fallback
    // entry before it has run), so look it up rather than hard-coding it.
    useEffect(() => {
        const menuid = revenueMenuId(userData?.permissions);
        if (menuid != null) setActiveMenuId(menuid);
    }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

    const userActivity = () => {
        let path = window.location.pathname.split("/");
        const pageName = path[path.length - 1];
        const payload = {
            "userid": localStorage.getItem("userId"),
            "pagename": pageName,
            "pageurl": window.location.href,
            "starttime": moment(),
            "useragent": JSON.parse(localStorage.getItem("loc"))
        };
        const previousId = localStorage.getItem("previousid");
        const urlLink = lambda + '/useractivity?appname=' + appname + (previousId ? "&previousid=" + previousId : "");
        axios({ method: 'POST', url: urlLink, data: payload })
            .then(function (response) {
                if (response?.data?.statusCode === 200) {
                    localStorage.setItem("previousid", response?.data?.result)
                }
            });
    }

    // Refetch whenever the range or the client/location changes.
    useEffect(() => {
        if (!dateFrom || !dateTo) return;
        let cancelled = false;
        setLoading(true);
        setLoadError("");
        const token = localStorage.getItem("token");
        const params = new URLSearchParams({ appname, token, fromDate: dateFrom, toDate: dateTo, timezone: TIMEZONE });
        if (client) params.set("clientid", client.value);
        if (location) params.set("storeid", location.value);
        axios({ method: 'POST', url: `${lambda}/revenueReport?${params.toString()}` })
            .then((response) => {
                if (cancelled) return;
                const result = response?.data?.result;
                if (result === "Invalid token or Expired") {
                    setShowSessionPopupup(true);
                    return;
                }
                setReport({ rows: result?.rows || [], summary: result?.summary || {}, plansIncluded: result?.plansIncluded !== false });
                setFilterOptions(toFilterOptions(result?.options));
            })
            .catch(() => { if (!cancelled) setLoadError("Failed to load the revenue report. Please try again."); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [dateFrom, dateTo, client, location]);

    const spanDays = useMemo(() => {
        if (!dateFrom || !dateTo) return 0;
        return moment(dateTo, DATE_FORMAT).diff(moment(dateFrom, DATE_FORMAT), "days") + 1;
    }, [dateFrom, dateTo]);

    // "Today" is a single day, so there is nothing to regroup. "Month" only helps once
    // the range spans more than one calendar month.
    const availableGranularities = useMemo(() => {
        if (datePreset === "today" || spanDays < 2) return [];
        return spanDays > 31 ? ["day", "week", "month"] : ["day", "week"];
    }, [datePreset, spanDays]);

    useEffect(() => {
        setGranularity(availableGranularities.includes("month") ? "month" : "day");
    }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

    const trendRows = useMemo(() => {
        const map = new Map();
        report.rows.forEach((r) => {
            if (!r?.date) return;
            const { key, label } = trendGroupKey(r.date, granularity);
            const cur = map.get(key) || TREND_FIELDS.reduce((acc, f) => ({ ...acc, [f]: 0 }), { key, label });
            TREND_FIELDS.forEach((f) => { cur[f] += Number(r[f]) || 0; });
            map.set(key, cur);
        });
        return Array.from(map.values()).sort((a, b) => b.key - a.key);
    }, [report.rows, granularity]);

    const totals = useMemo(() => trendRows.reduce(
        (acc, r) => TREND_FIELDS.reduce((a, f) => ({ ...a, [f]: a[f] + r[f] }), acc),
        TREND_FIELDS.reduce((acc, f) => ({ ...acc, [f]: 0 }), {}),
    ), [trendRows]);

    useEffect(() => { setPage(0); }, [trendRows, pageSize]);

    const summary = report.summary || {};
    // Reversals only exist on older refunds, so a column of $0.00 would just be noise.
    const hasReversals = (summary.clientShareReversed || 0) > 0;
    // Disputes are rare too: each line and column shows only when its own amount is not
    // zero, so an open dispute (fee only) shows no $0.00 'Disputes lost'.
    // A lost dispute is money out like a refund; Stripe's dispute fee is a cost from the
    // day the dispute opens, whatever the outcome.
    const disputeLost = Number(summary.disputeLost) || 0;
    const disputeFee = Number(summary.disputeFee) || 0;
    const hasDisputeLost = disputeLost > 0;
    const hasDisputeFee = disputeFee > 0;

    const collected = Number(summary.totalRevenue) || 0;
    const refunds = Math.abs(Number(summary.refund) || 0);
    const net = Number(summary.netRevenue) || 0;
    const paidOut = Number(summary.clientsRevenue) || 0;
    const pending = Number(summary.pendingPayout) || 0;
    const clientEarnings = paidOut + pending;
    const stripeFee = Number(summary.stripeFee) || 0;
    // Everything paid to Stripe: the estimated processing fee plus any dispute fees.
    const stripeCosts = stripeFee + disputeFee;
    const spotsaver = Number(summary.spotsaverRevenue) || 0;
    const bookings = Number(summary.bookingRevenue) || 0;
    const plans = Number(summary.planRevenue) || 0;

    // Net revenue is what gets split; a negative share (SpotSaver can go below zero after
    // fees on refunded days) keeps its figure but takes no room in the bar.
    const split = [
        { key: "clients", label: "To clients", value: clientEarnings, note: "Paid to clients' Stripe accounts" },
        { key: "stripe", label: "Stripe fees", value: stripeCosts, note: hasDisputeFee ? "Processing (estimated) + dispute fees" : "Estimated processing cost" },
        { key: "spotsaver", label: "SpotSaver keeps", value: spotsaver, note: "Plans + commission, after fees" },
    ];
    const splitBarTotal = split.reduce((acc, s) => acc + Math.max(s.value, 0), 0);

    const showChart = datePreset !== "today" && spanDays >= TREND_MIN_DAYS && trendRows.length > 0;
    const chartRows = useMemo(() => trendRows.slice().reverse().map((r) => ({
        label: r.label,
        clients: r.clientsRevenue + r.pendingPayout,
        stripe: r.stripeFee + r.disputeFee,
        spotsaver: Math.max(r.spotsaverRevenue, 0),
    })), [trendRows]);

    const pageCount = Math.max(1, Math.ceil(trendRows.length / pageSize));
    const pageRows = trendRows.slice(page * pageSize, page * pageSize + pageSize);
    const firstRow = trendRows.length === 0 ? 0 : page * pageSize + 1;
    const lastRow = Math.min(trendRows.length, (page + 1) * pageSize);

    const handleExport = () => {
        const header = ["Date", "Total Revenue", "Plan Revenue", "Booking Revenue", "Refund", ...(hasDisputeLost ? ["Disputes Lost"] : []), "Net Revenue",
            "Clients Revenue", "Pending Payouts", ...(hasReversals ? ["Client Share Reversed"] : []), "Stripe Fee", ...(hasDisputeFee ? ["Dispute Fee"] : []), "SpotSaver Revenue"];
        const rows = trendRows.map((r) => [r.label, r.totalRevenue, r.planRevenue, r.bookingRevenue, r.refund, ...(hasDisputeLost ? [r.disputeLost] : []), r.netRevenue,
            r.clientsRevenue, r.pendingPayout, ...(hasReversals ? [r.clientShareReversed] : []), r.stripeFee, ...(hasDisputeFee ? [r.disputeFee] : []), r.spotsaverRevenue]
            .map((v) => (typeof v === "number" ? v.toFixed(2) : v)));
        const scope = [client?.label, location?.label].filter(Boolean).join("-").replace(/[^a-z0-9-]+/gi, "_");
        downloadCsv(`spotsaver-revenue-${dateFrom}-to-${dateTo}${scope ? `-${scope}` : ""}.csv`, [header, ...rows]);
    };

    const kpis = [
        { label: "Client earnings", value: clientEarnings, note: "Total earned by clients", icon: "person", tone: "green" },
        { label: "Paid out", value: paidOut, note: "Transferred to Stripe accounts", icon: "check", tone: "green", valueTone: "positive" },
        { label: "Pending payouts", value: pending, note: "Owed, not yet transferred", icon: "schedule", tone: "amber" },
        { label: "Stripe fees", value: stripeCosts, note: hasDisputeFee ? `Processing (est.) + ${money(disputeFee)} dispute fees` : `Estimated · ${shortPercent(stripeFee, collected)} of total`, icon: "credit_card", tone: "lilac" },
    ];

    const collectedCols = 4 + (hasDisputeLost ? 1 : 0);
    const distributedCols = 4 + (hasReversals ? 1 : 0) + (hasDisputeFee ? 1 : 0);
    const tableCols = 1 + collectedCols + distributedCols;

    const renderCells = (r) => (
        <>
            <td className="rev-num rev-group-start"><Amount value={r.planRevenue} /></td>
            <td className="rev-num"><Amount value={r.bookingRevenue} /></td>
            <td className="rev-num"><Amount value={Math.abs(r.refund)} /></td>
            {hasDisputeLost && <td className="rev-num"><Amount value={r.disputeLost} /></td>}
            <td className="rev-num rev-strong"><Amount value={r.netRevenue} /></td>
            <td className="rev-num rev-group-start"><Amount value={r.clientsRevenue} /></td>
            <td className="rev-num"><Amount value={r.pendingPayout} /></td>
            {hasReversals && <td className="rev-num"><Amount value={r.clientShareReversed} /></td>}
            <td className="rev-num"><Amount value={r.stripeFee} /></td>
            {hasDisputeFee && <td className="rev-num"><Amount value={r.disputeFee} /></td>}
            <td className="rev-num rev-brand"><Amount value={r.spotsaverRevenue} /></td>
        </>
    );

    return (
        <>
            {showSessionPopupup && <SessionPopup />}
            <div id="layout-wrapper">
                <Header breadcrumb={["Finance", "Revenue"]} />
                <Sidebar />

                <div className="main-content revenue-scope revenue-page">
                    <div className="page-content">
                        <div className="container-fluid">

                            <RevenuePageHeader
                                title="Revenue"
                                subtitle="Where every dollar collected goes — clients, Stripe and SpotSaver."
                                onReset={filters.reset}
                                onExport={handleExport}
                                exportDisabled={loading || trendRows.length === 0}
                            />

                            <RevenueFilters
                                filters={filters}
                                clientOptions={filterOptions.clients}
                                locationOptions={filterOptions.locations}
                                presets={REVENUE_PRESETS}
                            />

                            {loadError && <div className="alert alert-danger">{loadError}</div>}

                            <div className={`rev-overview${loading ? " is-loading" : ""}`} aria-busy={loading}>
                                <section className="rev-card rev-hero">
                                    <div className="rev-hero-top">
                                        <div>
                                            <p className="rev-eyebrow">Total revenue collected</p>
                                            <p className="rev-hero-value">{money(collected)}</p>
                                        </div>
                                        <dl className="rev-hero-side">
                                            <div>
                                                <dt>Refunds</dt>
                                                <dd>{money(refunds)}</dd>
                                            </div>
                                            {hasDisputeLost && (
                                                <div>
                                                    <dt>Disputes lost</dt>
                                                    <dd>{money(disputeLost)}</dd>
                                                </div>
                                            )}
                                            <div>
                                                <dt>Net revenue</dt>
                                                <dd>{money(net)}</dd>
                                            </div>
                                        </dl>
                                    </div>

                                    <p className="rev-split-title">How it was split</p>
                                    <div className="rev-split-bar" role="img"
                                        aria-label={split.map((s) => `${s.label} ${money(s.value)}`).join(", ")}>
                                        {splitBarTotal > 0.005
                                            ? split.filter((s) => s.value > 0.005).map((s) => (
                                                <span key={s.key} style={{ flexGrow: s.value, background: SPLIT_COLORS[s.key] }} />
                                            ))
                                            : <span className="rev-split-empty" />}
                                    </div>

                                    <div className="rev-split-tiles">
                                        {split.map((s) => (
                                            <div className={`rev-split-tile rev-split-tile--${s.key}`} key={s.key}>
                                                <p className="rev-split-label">
                                                    <span className="rev-dot" style={{ background: SPLIT_COLORS[s.key] }} />{s.label}
                                                </p>
                                                <p className="rev-split-value">
                                                    <Amount value={s.value} className="rev-split-amount" />
                                                    <span className="rev-split-pct">{percent(s.value, net)}</span>
                                                </p>
                                                <p className="rev-split-note">{s.note}</p>
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                <section className="rev-card rev-sources">
                                    <p className="rev-eyebrow">Revenue sources</p>

                                    <div className="rev-source">
                                        <div className="rev-source-row">
                                            <span>Visitor bookings</span>
                                            <strong>{money(bookings)}</strong>
                                        </div>
                                        <div className="rev-meter"><span style={{ width: shortPercent(Math.max(bookings, 0), collected) }} /></div>
                                        <p className="rev-source-note">{shortPercent(bookings, collected)} of total</p>
                                    </div>

                                    <div className="rev-source">
                                        <div className="rev-source-row">
                                            <span>Client plans</span>
                                            <strong>{money(plans)}</strong>
                                        </div>
                                        <div className="rev-meter"><span style={{ width: shortPercent(Math.max(plans, 0), collected) }} /></div>
                                        <p className="rev-source-note">
                                            {report.plansIncluded
                                                ? `${shortPercent(plans, collected)} of total · subscriptions`
                                                : "Not tied to a location, so left out while one is selected"}
                                        </p>
                                    </div>

                                    <div className="rev-source-footer">
                                        <div className="rev-source-row rev-source-row--small">
                                            <span>Refunds issued</span>
                                            <strong className="revenue-negative">-{money(refunds)}</strong>
                                        </div>
                                        {hasDisputeLost && (
                                            <div className="rev-source-row rev-source-row--small" title="Disputes the visitor's bank decided in the visitor's favour">
                                                <span>Disputes lost</span>
                                                <strong className="revenue-negative">-{money(disputeLost)}</strong>
                                            </div>
                                        )}
                                        {hasDisputeFee && (
                                            <div className="rev-source-row rev-source-row--small" title="Stripe's fee for each dispute, charged when it opens and never returned">
                                                <span>Dispute fees</span>
                                                <strong className="revenue-negative">-{money(disputeFee)}</strong>
                                            </div>
                                        )}
                                        {hasReversals && (
                                            <div className="rev-source-row rev-source-row--small" title="Refunds the client funded through a Stripe transfer reversal">
                                                <span>Client share reversed</span>
                                                <strong className="revenue-negative">-{money(summary.clientShareReversed)}</strong>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            </div>

                            <div className={`rev-kpis${loading ? " is-loading" : ""}`}>
                                {kpis.map((k) => (
                                    <div className="rev-card rev-kpi" key={k.label}>
                                        <div className="rev-kpi-head">
                                            <p className="rev-kpi-label">{k.label}</p>
                                            <span className={`rev-kpi-icon rev-kpi-icon--${k.tone}`} aria-hidden="true">
                                                <span className="material-symbols-outlined">{k.icon}</span>
                                            </span>
                                        </div>
                                        <p className={`rev-kpi-value${k.valueTone ? ` rev-kpi-value--${k.valueTone}` : ""}`}>{money(k.value)}</p>
                                        <p className="rev-kpi-note">{k.note}</p>
                                    </div>
                                ))}
                            </div>

                            <section className="rev-card rev-breakdown">
                                <div className="rev-breakdown-head">
                                    <div>
                                        <h2 className="rev-breakdown-title">{GRANULARITY_TITLES[granularity]}</h2>
                                        <p className="rev-breakdown-subtitle">
                                            {showChart
                                                ? "Where the money went in each period."
                                                : `Pick ${TREND_MIN_DAYS} days or longer to see the trend chart.`}
                                        </p>
                                    </div>
                                    <div className="rev-breakdown-actions">
                                        {availableGranularities.length > 0 && (
                                            <div className="revenue-presets revenue-presets--small" role="group" aria-label="Group by">
                                                {availableGranularities.map((g) => (
                                                    <button
                                                        key={g}
                                                        type="button"
                                                        className={`revenue-preset${granularity === g ? " active" : ""}`}
                                                        aria-pressed={granularity === g}
                                                        onClick={() => setGranularity(g)}
                                                    >
                                                        {g}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        <button type="button" className="rev-link" onClick={() => navigate("/transactions")}>
                                            View transactions<span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
                                        </button>
                                    </div>
                                </div>

                                {showChart && (
                                    <div className="rev-chart">
                                        <div className="rev-chart-legend">
                                            {split.map((s) => (
                                                <span key={s.key}><span className="rev-dot" style={{ background: SPLIT_COLORS[s.key] }} />{s.label}</span>
                                            ))}
                                        </div>
                                        <ResponsiveContainer width="100%" height={240}>
                                            <BarChart data={chartRows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap="30%">
                                                <CartesianGrid vertical={false} stroke="#eeecf5" />
                                                <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: "#e2e0ec" }}
                                                    tick={{ fontSize: 12, fill: "#6b6a80" }} minTickGap={16} />
                                                <YAxis tickLine={false} axisLine={false} width={64}
                                                    tick={{ fontSize: 12, fill: "#6b6a80" }}
                                                    tickFormatter={(v) => money(v).replace(/\.00$/, "")} />
                                                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(74, 50, 216, 0.06)" }} />
                                                <Bar dataKey="clients" name="To clients" stackId="split" fill={SPLIT_COLORS.clients} stroke="#fff" strokeWidth={1} />
                                                <Bar dataKey="stripe" name="Stripe fees" stackId="split" fill={SPLIT_COLORS.stripe} stroke="#fff" strokeWidth={1} />
                                                <Bar dataKey="spotsaver" name="SpotSaver keeps" stackId="split" fill={SPLIT_COLORS.spotsaver} stroke="#fff" strokeWidth={1} radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}

                                <div className="rev-table-wrap">
                                    <table className="rev-table">
                                        <thead>
                                            <tr className="rev-table-groups">
                                                <th rowSpan={2} className="rev-table-date">Date</th>
                                                <th colSpan={collectedCols} className="rev-group-start">Collected</th>
                                                <th colSpan={distributedCols} className="rev-group-start">Distributed</th>
                                            </tr>
                                            <tr>
                                                <th className="rev-num rev-group-start">Plans</th>
                                                <th className="rev-num">Bookings</th>
                                                <th className="rev-num">Refunds</th>
                                                {hasDisputeLost && <th className="rev-num">Disputes</th>}
                                                <th className="rev-num rev-strong">Net</th>
                                                <th className="rev-num rev-group-start">Clients</th>
                                                <th className="rev-num">Pending</th>
                                                {hasReversals && <th className="rev-num">Reversed</th>}
                                                <th className="rev-num">Stripe fee</th>
                                                {hasDisputeFee && <th className="rev-num">Dispute fee</th>}
                                                <th className="rev-num rev-strong">SpotSaver</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr><td colSpan={tableCols}><TableLoader /></td></tr>
                                            ) : pageRows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={tableCols} className="rev-empty">
                                                        <span className="material-symbols-outlined" aria-hidden="true">summarize</span>
                                                        No revenue for the selected filters
                                                    </td>
                                                </tr>
                                            ) : pageRows.map((r) => (
                                                <tr key={r.key}>
                                                    <td className="rev-table-date">{r.label}</td>
                                                    {renderCells(r)}
                                                </tr>
                                            ))}
                                        </tbody>
                                        {!loading && trendRows.length > 0 && (
                                            <tfoot>
                                                <tr>
                                                    <td className="rev-table-date">Total</td>
                                                    {renderCells(totals)}
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>

                                <div className="rev-pagination">
                                    <label>
                                        Rows per page
                                        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                                            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                    </label>
                                    <span>{firstRow}–{lastRow} of {trendRows.length}</span>
                                    <div className="rev-pagination-nav">
                                        <button type="button" aria-label="Previous page" disabled={page === 0} onClick={() => setPage(page - 1)}>
                                            <span className="material-symbols-outlined">chevron_left</span>
                                        </button>
                                        <button type="button" aria-label="Next page" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)}>
                                            <span className="material-symbols-outlined">chevron_right</span>
                                        </button>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>

                    <Footer />
                </div>
            </div>
        </>
    );
};

export default Revenue;
